import { getDownloadURL, ref } from 'firebase/storage';
import { AudioLines, GripHorizontal, Pencil, Star, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { storage } from '../../../firebase';
import type { VoiceOption } from '../services/settingsTypes';
import { VOICE_ICONS } from './voiceIcons';

interface VoiceRowProps {
  voice: VoiceOption;
  index: number;
  isDefault: boolean;
  isDragging: boolean;
  isGeneratingPreview: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onGeneratePreview: (voice: VoiceOption) => void;
  onMakeDefault: (voice: VoiceOption) => void;
  onEdit: (voice: VoiceOption) => void;
  onDelete: (voice: VoiceOption) => void;
}

const Badge = ({ className, children }: { className: string; children: React.ReactNode }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded ${className}`}>{children}</span>
);

export const VoiceRow = ({
  voice,
  index,
  isDefault,
  isDragging,
  isGeneratingPreview,
  onDragStart,
  onDragOver,
  onDragEnd,
  onGeneratePreview,
  onMakeDefault,
  onEdit,
  onDelete,
}: VoiceRowProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const VoiceIcon = VOICE_ICONS[voice.icon] ?? AudioLines;

  useEffect(() => {
    setPreviewUrl(null);
    if (!voice.previewPath) return;
    let isCurrent = true;
    getDownloadURL(ref(storage, voice.previewPath))
      .then((url) => { if (isCurrent) setPreviewUrl(url); })
      .catch((err) => console.error('Failed to load voice preview URL:', err));
    return () => { isCurrent = false; };
  }, [voice.previewPath]);

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={`bg-white border border-gray-200 rounded-lg p-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-md transition-shadow cursor-move ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-4 flex-1 min-w-0">
        <div className="text-gray-400">
          <GripHorizontal size={16} />
        </div>
        <VoiceIcon size={18} className="text-gray-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-900">{voice.name}</span>
            <span className="text-xs text-gray-500 break-all">({voice.id} → {voice.voiceId})</span>
            {isDefault && <Badge className="bg-blue-100 text-blue-700">Default</Badge>}
            {!voice.isPublished && <Badge className="bg-gray-100 text-gray-600">Unpublished</Badge>}
            {!voice.previewPath && <Badge className="bg-yellow-100 text-yellow-800">No preview</Badge>}
            {!voice.icon && <Badge className="bg-yellow-100 text-yellow-800">No icon</Badge>}
          </div>
          {voice.description && <div className="text-xs text-gray-500 truncate">{voice.description}</div>}
        </div>
        {previewUrl && <audio controls src={previewUrl} className="h-8 w-full sm:w-auto" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onGeneratePreview(voice)}
          size="sm"
          variant="secondary"
          loading={isGeneratingPreview}
          icon={<AudioLines size={16} />}
        >
          {voice.previewPath ? 'Regenerate preview' : 'Generate preview'}
        </Button>
        {!isDefault && (
          <Button
            onClick={() => onMakeDefault(voice)}
            size="sm"
            variant="outline"
            title="Make default"
            icon={<Star size={16} />}
          />
        )}
        <Button onClick={() => onEdit(voice)} size="sm" icon={<Pencil size={16} />} />
        <Button onClick={() => onDelete(voice)} variant="danger" size="sm" icon={<Trash size={16} />} />
      </div>
    </div>
  );
};
